import type { ApiFetch } from "@/lib/restClient";

/**
 * Client side of the REST list contract (backend `persistence/listing.py`).
 *
 * Every collection endpoint answers one page: `first` rows (50 by default, 500
 * at most) after the opaque `after` cursor, searched with `q`, sorted with
 * `sort`/`order` and filtered by the resource's declared filters (several
 * values comma-separated). The rows stay under the resource's historical key
 * (`tenants`, `network_ids`...), next to `page_info` and `facets`.
 */
export const DEFAULT_PAGE_SIZE = 25;
export const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
export const MAXIMUM_PAGE_SIZE = 500;

/** Validate browser preferences before using them as REST page sizes. */
export function normalizePageSize(value: unknown): number {
  return PAGE_SIZE_OPTIONS.find((size) => String(size) === String(value)) ?? DEFAULT_PAGE_SIZE;
}

export interface PageInfo {
  has_next_page: boolean;
  end_cursor: string | null;
  /** Rows matching the search and filters. */
  total_count: number;
  /** Rows readable before the search and filters. */
  unfiltered_count: number;
}

export interface FacetOption {
  value: string;
  label: string;
  count: number;
}

export type Facets = Record<string, FacetOption[]>;

export type PagedResponse<Key extends string, Row> = {
  [K in Key]: Row[];
} & {
  count: number;
  page_info: PageInfo;
  facets?: Facets;
};

/** Search, sort and filters of one list request; empty values are omitted. */
export interface ListQuery {
  q?: string;
  sort?: string;
  order?: "asc" | "desc";
  filters?: Record<string, string | readonly string[] | number | null | undefined>;
}

/** `path` with the list query, page size and cursor appended. */
export function listPath(
  path: string,
  query: ListQuery = {},
  page: { first?: number; after?: string | null } = {},
): string {
  const [base, existing = ""] = path.split("?");
  const params = new URLSearchParams(existing);
  if (query.q?.trim()) params.set("q", query.q.trim());
  if (query.sort) params.set("sort", query.sort);
  if (query.order) params.set("order", query.order);
  for (const [name, value] of Object.entries(query.filters ?? {})) {
    const values = Array.isArray(value) ? value : value == null ? [] : [String(value)];
    const joined = values.filter((entry) => entry !== "").join(",");
    if (joined) params.set(name, joined);
  }
  if (page.first !== undefined) params.set("first", String(page.first));
  if (page.after) params.set("after", page.after);
  const search = params.toString();
  return search ? `${base}?${search}` : base;
}

/**
 * Every row of a collection, following cursors with the largest page size.
 *
 * For reference data that screens resolve ids against (tenant names, accounts,
 * the widget catalog) and for screens whose filters depend on code the
 * interface ships: those still page what they display (see `useClientPages`).
 */
export async function fetchAllPages<Row, Key extends string = string>(
  apiFetch: ApiFetch,
  path: string,
  key: Key,
  { signal, query }: { signal?: AbortSignal; query?: ListQuery } = {},
): Promise<Row[]> {
  const rows: Row[] = [];
  let after: string | null = null;
  // A bounded loop: a server that keeps answering "next page" with the same
  // cursor must not hang the interface.
  for (let pages = 0; pages < 1000; pages += 1) {
    const response: PagedResponse<Key, Row> = await apiFetch<PagedResponse<Key, Row>>(
      listPath(path, query, { first: MAXIMUM_PAGE_SIZE, after }),
      { signal },
    );
    rows.push(...(response[key] ?? []));
    const next = response.page_info?.has_next_page ? response.page_info.end_cursor : null;
    if (!next || next === after) return rows;
    after = next;
  }
  return rows;
}

/** "1–50 of 312" for the rows of one page. */
export function pageSummary(offset: number, shown: number, total: number): string {
  if (total === 0 || shown === 0) return `0 of ${total}`;
  return `${offset + 1}–${offset + shown} of ${total}`;
}
